import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {Alert, Button, Dropdown, Form, Input, Menu, message, Modal, Popconfirm, Select, Space, Spin} from "antd";
import NiceModal from "@ebay/nice-modal-react";
import {NiceModalForm} from "../../components/Modals";
import {callAndReload, callWithCatch, idNameToOption, pp, tourneyUrl} from "../../utils";
import FakeTable from "../../components/FakeTable";
import FormSelector from "../../components/FormSelector";
import axios from "axios";
import {PlayerStore} from "../rt/PlayerDrawer";
import User from "../../components/User";
import {Showable} from "../../components/Switcher";
import {CopyOutlined, DownOutlined} from "@ant-design/icons";
import EditTeamCapAndName from "./EditTeamCapAndName";
import {PlayerUnWithdrawer} from "./Registration";
import {useEffectOnce} from "react-use";
import store from "./store";

// Success handler - shows message and reloads appropriately based on user type
const handleSuccess = (successMessage, modalToClose = null) => {
  if (modalToClose) {
    NiceModal.remove(modalToClose);
  }

  if (User.admin) {
    message.success(successMessage);
    PlayerStore.reload();
    store.triggerRefresh('reg_change');
  } else {
    Modal.success({
      content: successMessage,
      onOk: () => window.location.reload()
    });
  }
};

// Admin-specific modals

// Replace Player Modal (for admin)
export const ReplacePlayerModal = NiceModal.create(observer(({pl, r}) => {
  const [data, setData] = useState(false);

  let load = () => {
    callWithCatch({
      url: `/registrations/${r.id}/edit`,
      method: 'get',
      onSuccess: data => setData(data),
    })
  }

  let replace = async (params) => {
    await callWithCatch({
      url: `/registrations/${r.id}`,
      method: 'put',
      params,
      reload: true,
      onSuccess: () => handleSuccess("Player replaced successfully!", ReplacePlayerModal)
    })
  }

  useEffectOnce(load);

  return <NiceModalForm width={400} okText="Replace" onSave={replace} title={"Replace " + pl.name} zIndex={1005}>
      <FormSelector showSearch
                    name="replace_with_player_id"
                    autoComplete="new-state"
                    loading={!data}
                    label="Replace With" width="300px"
                    filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())}
                    options={data ? data.single_players.map(pl => idNameToOption(pl)) : []} />

    { data && !data.single_players.length && <Alert type="warning" message="No Players Available" />}
  </NiceModalForm>
}));

// Create Team Modal - for Team4 and TeamBig
export const CreateTeamModal = NiceModal.create(observer(({r}) => {
  const [createdTeam, setCreatedTeam] = useState(null);

  let onSave = async (params) => {
    await callWithCatch({
      url: `/registrations/${r.id}`,
      params: { team: 'create', ...params },
      method: 'put',
      reload: false,
      onSuccess: (data) => {
        if (User.admin) {
          // For admin, just show success and reload
          handleSuccess("Team created successfully!", CreateTeamModal);
        } else {
          // For player, fetch the team to get UUID for sharing
          axios.get(`/registrations/${r.id}/edit`).then(response => {
            // Find the team we just created - it should be the one with our player
            const teams = response.data.teams;
            const playerId = gon.player?.id;
            const myTeam = teams.find(t => t.players.some(p => p.id === playerId) && t.uuid);
            if (myTeam && myTeam.uuid) {
              setCreatedTeam(myTeam);
            } else {
              // If we can't find the team with UUID, just reload
              handleSuccess("Team created successfully!", CreateTeamModal);
            }
          }).catch(err => {
            // If fetch fails, still reload to show the team
            handleSuccess("Team created successfully!", CreateTeamModal);
          });
        }
      }
    });

    // Don't close modal for player context - wait for team code display
    // For admin, modal will close automatically
    return User.admin;
  };

  let copyToClipboard = () => {
    navigator.clipboard.writeText(createdTeam.uuid);
    message.success("Team code copied to clipboard!");
  };

  let closeAndReload = () => {
    handleSuccess("Team created successfully!", CreateTeamModal);
  };

  if (createdTeam) {
    return <NiceModalForm
      footer={<Button type="primary" onClick={closeAndReload}>Done</Button>}
      title="Team Created Successfully"
      width={400}
      zIndex={1005}
    >
      <div className="space-y-4">
        <Alert
          message="Share this code with your teammates!"
          description="They will need this code to join your team. Keep it private."
          type="success"
          showIcon
        />
        <div className="flex items-center justify-center space-x-2 p-4 bg-gray-100 rounded">
          <code className="text-2xl font-bold">{createdTeam.uuid}</code>
          <Button
            icon={<CopyOutlined />}
            onClick={copyToClipboard}
            size="small"
          />
        </div>
        <div className="text-sm text-gray-600">
          Your teammates can join by selecting your team and entering this code.
        </div>
      </div>
    </NiceModalForm>;
  }

  return <NiceModalForm
    width={350}
    onSave={onSave}
    title="Create Team"
    okText="Create Team"
    initialValues={{team_name: `${(r.player || gon.player).fname}'s team`}}
    zIndex={1005}
  >
    <Form.Item name="team_name" label="Team Name" rules={[{required: true, message: "Team name is required"}]}>
      <Input autoFocus placeholder="e.g. Thunder Squad" />
    </Form.Item>
    <Alert
      message="After creating the team, you'll receive a team code to share with your teammates."
      type="info"
    />
  </NiceModalForm>;
}));

// Join Team Modal - for Team4 and TeamBig
export const JoinTeamModal = NiceModal.create(observer(({r}) => {
  const [teams, setTeams] = useState(null);

  useEffect(() => {
    let load = async () => {
      let data = (await axios.get(`/registrations/${r.id}/edit`)).data;
      setTeams(data.teams);
    };
    load();
  }, [r]);

  let onSave = async (params) => {
    if (!params.team_id) {
      message.error("Please select a team");
      return false;
    }

    await callWithCatch({
      url: `/registrations/${r.id}`,
      params: { team: params.team_id, team_code: User.admin ? null : params.team_code },
      method: 'put',
      onSuccess: () => handleSuccess("Joined team successfully!", JoinTeamModal)
    });
  };

  // Create options for the select with searchable text
  let teamOptions = teams?.map(t => {
    const playerNames = t.players.map(pl => pl.name).join(", ");
    const teamName = t.name || "Unnamed Team";
    const searchText = `${teamName} ${playerNames}`.toLowerCase();

    return {
      value: t.id,
      label: (
        <div>
          <div className="font-medium">{teamName}</div>
          <div className="text-sm text-gray-500">{playerNames}</div>
        </div>
      ),
      searchText: searchText
    };
  }) || [];

  return <NiceModalForm
    width={400}
    onSave={onSave}
    title="Join Team"
    okText="Join Team"
    initialValues={{}}
    zIndex={1005}
  >
    <Spin spinning={!teams}>
      {teams && teams.length > 0 ? (
        <>
          <div className="space-y-4">
            <Form.Item
              name="team_id"
              label="Select Team"
              rules={[{required: true, message: "Please select a team"}]}
            >
              <Select
                showSearch
                placeholder="Search for team or player names"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.searchText.includes(input.toLowerCase())
                }
                style={{width: "100%"}}
                options={teamOptions}
              />
            </Form.Item>
            {!User.admin && (
              <Form.Item
                name="team_code"
                label="Team Code"
                extra="Get the code from your captain/teammates"
                rules={[{required: true, message: "Team code is required"}]}
              >
                <Input placeholder="Enter the team code" maxLength={10} />
              </Form.Item>
            )}
          </div>
        </>
      ) : teams && teams.length === 0 ? (
        <Alert
          message="No teams available"
          description="There are no teams with available spots at this time."
          type="warning"
        />
      ) : null}
    </Spin>
  </NiceModalForm>;
}));

// Leave Team Modal
export const LeaveTeamModal = NiceModal.create(observer(({r}) => {
  const [teamType, setTeamType] = useState(null);

  useEffect(() => {
    let load = async () => {
      let data = (await axios.get(`/registrations/${r.id}/edit`)).data;
      setTeamType(data.team_type);
    };
    load();
  }, [r]);

  let onConfirm = async () => {
    // For doubles, break up team but keep "has partner" status
    const params = teamType === "doubles"
      ? { partner_reg_id: "has" }
      : { team: 'remove' };

    await callWithCatch({
      url: `/registrations/${r.id}`,
      params: params,
      method: 'put',
      onSuccess: () => handleSuccess(
        teamType === "doubles" ? "Team broken up successfully!" : "Left team successfully!",
        LeaveTeamModal
      )
    });
  };

  const isDoubles = teamType === "doubles";
  const title = isDoubles ? "Break Up Team" : "Leave Team";
  const message = isDoubles
    ? "Are you sure you want to break up this team? Both players will be set to 'Has Partner' status."
    : "Are you sure you want to leave this team?";
  const buttonText = isDoubles ? "Break Up Team" : "Leave Team";

  return <Modal
    visible={true}
    title={title}
    onCancel={() => NiceModal.remove(LeaveTeamModal)}
    zIndex={1005}
    footer={[
      <Button key="cancel" onClick={() => NiceModal.remove(LeaveTeamModal)}>
        Cancel
      </Button>,
      <Button key="leave" type="primary" danger onClick={onConfirm} loading={teamType === null}>
        {buttonText}
      </Button>
    ]}
  >
    <div className="space-y-2">
      <p>{message}</p>
      {r.with && <p className="text-gray-600">{r.with}</p>}
    </div>
  </Modal>;
}));

// Withdraw Modal
export const WithdrawModal = NiceModal.create(observer(({r}) => {
  let onConfirm = async () => {
    await callWithCatch({
      url: `/registrations/${r.id}`,
      params: { withdraw: true },
      method: 'put',
      onSuccess: () => handleSuccess("Withdrawn successfully!", WithdrawModal)
    });
  };

  return <Modal
    visible={true}
    title="Withdraw from Event"
    onCancel={() => NiceModal.remove(WithdrawModal)}
    zIndex={1005}
    footer={[
      <Button key="cancel" onClick={() => NiceModal.remove(WithdrawModal)}>
        Cancel
      </Button>,
      <Button key="withdraw" type="primary" danger onClick={onConfirm}>
        Withdraw
      </Button>
    ]}
  >
    <div className="space-y-2">
      <p>Are you sure you want to withdraw from this event?</p>
      <p className="text-sm text-gray-600">{r.division_name}</p>
    </div>
  </Modal>;
}));

// Change Division Modal
export const ChangeDivisionModal = NiceModal.create(observer(({r}) => {
  const [divs, setDivs] = useState(null);

  useEffect(() => {
    let load = async () => {
      let data = (await axios.get(`/registrations/${r.id}/edit`)).data;
      setDivs(data.divisions);
    };
    load();
  }, [r]);

  let onSave = async (params) => {
    await callWithCatch({
      url: `/registrations/${r.id}`,
      method: 'put',
      params: params,
      onSuccess: () => handleSuccess("Division changed successfully!", ChangeDivisionModal)
    });
  };

  return <NiceModalForm
    zIndex={1005}
    onSave={onSave}
    initialValues={{division_id: r.division_id}}
    title="Change Division"
    width={350}
  >
    <Spin spinning={!divs}>
      {divs && divs.length > 0 ? (
        <FormSelector
          label="New Division"
          name="division_id"
          options={divs.map(d => [d.id, d.name])}
        />
      ) : divs && divs.length === 0 ? (
        <Alert
          message="No divisions available"
          description="There are no other divisions you can move to."
          type="warning"
        />
      ) : null}
    </Spin>
  </NiceModalForm>;
}));

// Change Partner Modal (for doubles) - moved from Registration.js
export const ChangePartnerModal = NiceModal.create(observer(({r}) => {
  let [updating, setUpdating] = useState(false);
  let [player, setPlayer] = useState([]);
  let [partners, setPartners] = useState([]);
  let [teamType, setTeamType] = useState([]);

  useEffect(() => {
    let load = async () => {
      let data = (await axios.get(`/registrations/${r.id}/edit`)).data;

      pp(data)

      setPlayer(data.player);
      setPartners(data.partners);
      setTeamType(data.team_type);
    };
    load();
  }, [r]);

  let update = (registrationId) => {
    callWithCatch({
      setLoading: setUpdating,
      url: `/registrations/${r.id}`,
      method: 'put',
      params: { partner_reg_id: registrationId },
      onSuccess: () => handleSuccess("Partner changed successfully!", ChangePartnerModal)
    });
  };

  let isDoubles = teamType === "doubles";
  let needJoin = !r.team || r.alone || r.team?.players?.length === 1;

  let headerText = needJoin ? "Set Partner" : "Change Partner";

  let playersChoice = () => {
    return <div className="overflow-y-auto" style={{maxHeight: "510px"}}>
      <FakeTable
        cs="ant-table-small"
        data={partners.map(p => [
          p.name,
          <Button size="small" block onClick={() => update(p.id)} type="primary">
            Partner with {p.name.split(/ /)[0]}
          </Button>
        ])}
      />
    </div>;
  };

  let inner = <div>
    <Showable v={!!partners?.length}>
      <Alert type="warning" message="Make sure they want to partner together before partnering up!" />
      <br/>
      <div className="font-bold">Select Partner:</div>
    </Showable>
    {!partners?.length && <Alert type="warning" message="No players without a partner" />}
    {playersChoice()}
  </div>;

  return <NiceModalForm
    footer={null}
    top
    zIndex={1005}
    title={headerText + " for " + player.name}
    width={500}
  >
    <Spin spinning={updating}>
      {inner}
    </Spin>
  </NiceModalForm>;
}));

// Action Button Component
export const PlayerActionButton = observer(({r, action, ...props}) => {
  const handleClick = () => {
    switch (action) {
      case 'create_team':
        NiceModal.show(CreateTeamModal, {r});
        break;
      case 'join_team':
        NiceModal.show(JoinTeamModal, {r});
        break;
      case 'leave_team':
        NiceModal.show(LeaveTeamModal, {r});
        break;
      case 'withdraw':
        NiceModal.show(WithdrawModal, {r});
        break;
      case 'change_division':
        NiceModal.show(ChangeDivisionModal, {r});
        break;
      case 'change_partner':
        NiceModal.show(ChangePartnerModal, {r});
        break;
      case 'edit_team':
        if (r.team) {
          NiceModal.show(EditTeamCapAndName, {t: r.team});
        } else {
          message.error("No team to edit");
        }
        break;
      default:
        console.warn('Unknown action:', action);
    }
  };

  const getActionLabel = (action) => {
    // Special case for change_partner - check if player has a team/partner
    if (action === 'change_partner') {
      const hasPartner = r.team_id;
      return hasPartner ? "Change Partner" : "Set Partner";
    }

    const actionLabels = {
      create_team: "Create Team",
      join_team: "Join Team",
      leave_team: "Leave Team",
      withdraw: "Withdraw",
      change_division: "Change Division",
      edit_team: "Edit Team"
    };
    return actionLabels[action] || action;
  };

  const buttonSize = User.admin ? 'small' : undefined;
  const buttonProps = action === 'leave_team'
    ? { type: "link", danger: true, size: buttonSize, ...props }
    : { size: buttonSize, ...props };

  return (
    <Button onClick={handleClick} {...buttonProps}>
      {getActionLabel(action)}
    </Button>
  );
});

// Render all actions for a registration
export const PlayerActions = observer(({r}) => {
  if (!r.player_actions || r.player_actions.length === 0) {
    return null;
  }

  return (
    <Space size="small" wrap direction="horizontal">
      {r.player_actions.map(action => (
        <PlayerActionButton key={action} r={r} action={action} />
      ))}
    </Space>
  );
});

// Admin Action Button Component
export const AdminPlayerActionButton = observer(({r, pl, action, ...props}) => {
  // If action is part of player_actions, reuse PlayerActionButton
  const playerActions = ['create_team', 'join_team', 'leave_team', 'edit_team', 'change_partner', 'withdraw', 'change_division'];

  // Map admin action names to player action names
  const actionMapping = {
    'leave': 'leave_team',
    'div_change': 'change_division'
  };

  const mappedAction = actionMapping[action] || action;

  if (playerActions.includes(mappedAction)) {
    return <PlayerActionButton r={r} action={mappedAction} {...props} />;
  }

  // Handle admin-only actions
  const handleClick = () => {
    switch (action) {
      case 'unwithdraw':
        // Use existing PlayerUnWithdrawer component (rendered inline)
        break;
      case 'replace':
        NiceModal.show(ReplacePlayerModal, {r, pl});
        break;
      case 'why_locked':
        Modal.error({
          zIndex: 2000,
          title: "No Other Actions",
          content: "Can't do other actions because the player is a part of a scheduled division which is locked. Reset the division to release."
        });
        break;
      case 'move_to_waitlist':
        // Handled via Popconfirm (rendered inline)
        break;
      default:
        console.warn('Unknown admin action:', action);
    }
  };

  // Handle special cases that need custom rendering
  if (action === 'unwithdraw') {
    return <PlayerUnWithdrawer r={r} />;
  }

  if (action === 'move_to_waitlist') {
    return <Popconfirm
      title="Move to Waitlist"
      description="Are you sure you want to move this player to the waitlist?"
      onConfirm={() => {
        callWithCatch({
          url: `/registrations/${r.id}`,
          method: 'put',
          params: { move_to_waitlist: true },
          onSuccess: () => {
            PlayerStore.reload();
            store.triggerRefresh('reg_change');
          }
        })
      }}
      okText="Yes"
      cancelText="No"
    >
      <Button size="small" type="link">Move to Waitlist</Button>
    </Popconfirm>;
  }

  // For actions that use modals
  const actionLabels = {
    div_change: "Change Division",
    replace: "Replace",
    why_locked: "Other Actions"
  };

  const buttonProps = action === 'why_locked'
    ? { type: "link", size: "small", ...props }
    : { size: "small", ...props };

  return (
    <Button onClick={handleClick} {...buttonProps}>
      {actionLabels[action] || action}
    </Button>
  );
});

// Render all admin actions for a registration
export const AdminPlayerActions = observer(({r, pl}) => {
  if (!r.actions || r.actions.length === 0) {
    return null;
  }

  // If only one action, render it directly
  if (r.actions.length === 1) {
    return <AdminPlayerActionButton r={r} pl={pl} action={r.actions[0]} />;
  }

  // Get action labels
  const getActionLabel = (action) => {
    // Special case for change_partner - check if player has a team/partner
    if (action === 'change_partner') {
      const hasPartner = r.team_id;
      return hasPartner ? "Change Partner" : "Set Partner";
    }

    const actionLabels = {
      unwithdraw: "Unwithdraw",
      withdraw: "Withdraw",
      replace: "Replace",
      why_locked: "Other Actions",
      leave: "Leave Team",
      change_division: "Change Division",
      create_team: "Create Team",
      join_team: "Join Team",
      move_to_waitlist: "Move to Waitlist"
    };
    return actionLabels[action] || action;
  };

  // Handle action clicks for menu items
  const handleMenuClick = (action) => {
    const actionMapping = {
      'leave': 'leave_team',
      'div_change': 'change_division'
    };
    const mappedAction = actionMapping[action] || action;

    switch (action) {
      case 'unwithdraw':
        // Not typically in menu, but handle if needed
        break;
      case 'replace':
        NiceModal.show(ReplacePlayerModal, {r, pl});
        break;
      case 'why_locked':
        Modal.error({
          zIndex: 2000,
          title: "No Other Actions",
          content: "Can't do other actions because the player is a part of a scheduled division which is locked. Reset the division to release."
        });
        break;
      case 'withdraw':
        NiceModal.show(WithdrawModal, {r});
        break;
      case 'leave':
        NiceModal.show(LeaveTeamModal, {r});
        break;
      case 'change_partner':
        NiceModal.show(ChangePartnerModal, {r});
        break;
      case 'change_division':
        NiceModal.show(ChangeDivisionModal, {r});
        break;
      case 'create_team':
        NiceModal.show(CreateTeamModal, {r});
        break;
      case 'join_team':
        NiceModal.show(JoinTeamModal, {r});
        break;
      case 'edit_team':
        if (r.team) {
          NiceModal.show(EditTeamCapAndName, {t: r.team});
        } else {
          message.error("No team to edit");
        }
        break;
      case 'move_to_waitlist':
        // Handled via modal confirm below
        Modal.confirm({
          title: "Move to Waitlist",
          content: "Are you sure you want to move this player to the waitlist?",
          zIndex: 1005,
          onOk: () => {
            callWithCatch({
              url: `/registrations/${r.id}`,
              method: 'put',
              params: { move_to_waitlist: true },
              onSuccess: () => {
                PlayerStore.reload();
                store.triggerRefresh('reg_change');
              }
            });
          },
          okText: "Yes",
          cancelText: "No"
        });
        break;
      default:
        console.warn('Unknown action:', action);
    }
  };

  // First action becomes the primary button
  const [primaryAction, ...menuActions] = r.actions;

  // Handle primary button click
  const handlePrimaryClick = () => {
    handleMenuClick(primaryAction);
  };

  // Build menu items for dropdown
  const menuItems = menuActions.map(action => ({
    key: action,
    label: getActionLabel(action),
    onClick: () => handleMenuClick(action)
  }));

  return (
    <Dropdown.Button
      size="small"
      onClick={handlePrimaryClick}
      menu={{ items: menuItems }}
      icon={<DownOutlined />}
    >
      {getActionLabel(primaryAction)}
    </Dropdown.Button>
  );
});
